"use client";

import {
  EmptyState,
  Icon,
  SectionCard,
  StatusBadge,
  cn,
} from "@repo/dashboard-core";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";

import { DEFAULT_ESIM_CURRENCY } from "@/config/esim";
import {
  esimLifecycle,
  type EsimProduct,
  type RecentEsim,
} from "@/lib/types/esim";
import {
  countryFlag,
  formatEsimDate,
  formatIccid,
  orderDataAllowance,
  validityUnitKey,
} from "@/lib/utils/esim";

import { EsimDetailModal } from "./EsimDetailModal";
import { OrderModal } from "./OrderModal";

const LIFECYCLE_VARIANT = {
  ready: "info",
  installed: "warning",
  active: "success",
} as const;

/**
 * "Your recent eSIM" — the eSIM customer's MemberZone home (GD-98), and the
 * eSIM list the top-up home shows below the top-ups when there are any (GD-135).
 *
 * Mirrors the top-up list: the magic link names the order just bought with
 * `?id=`, and clicking that eSIM asks for the Trustpilot rating. After that,
 * like any delivered eSIM, it opens its details.
 *
 * The shell loads the list (`PurchasesProvider`), since what the customer
 * bought decides which home this is.
 */
export function RecentEsims({
  esims,
  failed,
  feedbackApplicationId,
  onFeedback,
}: {
  esims: RecentEsim[];
  failed: boolean;
  /** The `?id=` the magic link carried, empty when there is none. */
  feedbackApplicationId: string;
  /** Asks for the rating; false once it has been asked. */
  onFeedback: () => boolean;
}) {
  const t = useTranslations("dashboard");
  const tEsim = useTranslations("esim");
  const [detail, setDetail] = useState<RecentEsim | null>(null);
  const [topUpPlan, setTopUpPlan] = useState<EsimProduct | null>(null);

  if (failed || esims.length === 0) {
    return (
      <SectionCard>
        <EmptyState
          icon={<Icon name={failed ? "alert" : "esim"} size={28} />}
          title={tEsim(failed ? "list-error" : "list-empty")}
          description={tEsim(
            failed ? "list-error-description" : "list-empty-description",
          )}
        />
      </SectionCard>
    );
  }

  const rates = (esim: RecentEsim) =>
    !!feedbackApplicationId && esim.applicationId === feedbackApplicationId;
  // An undelivered eSIM has no ICCID, so no details to open.
  const opens = (esim: RecentEsim) => rates(esim) || !!esim.iccid;

  // The eSIM the magic link names asks for the rating on its first click; any
  // later click, and any click on another delivered eSIM, opens its details.
  const open = (esim: RecentEsim) => {
    if (rates(esim) && onFeedback()) return;
    if (esim.iccid) setDetail(esim);
  };

  // On wider screens the rows share one set of columns, so the allowances and
  // statuses line up however long each status reads in the locale. The
  // chevron's column only exists when a row has one.
  return (
    <SectionCard title={t("esim.recent")}>
      <div
        className={cn(
          "space-y-1 sm:grid sm:gap-x-4 sm:gap-y-1 sm:space-y-0",
          esims.some(opens)
            ? "sm:grid-cols-[minmax(0,1fr)_auto_auto_auto]"
            : "sm:grid-cols-[minmax(0,1fr)_auto_auto]",
        )}
      >
        {esims.map((esim, index) => (
          <RecentEsimRow
            key={esim.iccid ?? esim.applicationId ?? index}
            esim={esim}
            onOpen={opens(esim) ? () => open(esim) : undefined}
          />
        ))}
      </div>

      {/* The same details and recharge as the Esim page, on the wallet the
          eSIM was bought on. */}
      <EsimDetailModal
        iccid={topUpPlan ? null : (detail?.iccid ?? null)}
        currency={detail?.currency ?? DEFAULT_ESIM_CURRENCY}
        onClose={() => setDetail(null)}
        onTopUp={setTopUpPlan}
      />
      <OrderModal
        product={topUpPlan}
        currency={detail?.currency ?? DEFAULT_ESIM_CURRENCY}
        iccid={detail?.iccid ?? undefined}
        onClose={() => {
          setTopUpPlan(null);
          setDetail(null);
        }}
        // ponytail: the list shows the plan bought, which a recharge leaves as
        // it was; reload the purchases here if the list ever shows the balance.
        onCompleted={() => {}}
      />
    </SectionCard>
  );
}

function RecentEsimRow({
  esim,
  onOpen,
}: {
  esim: RecentEsim;
  onOpen?: () => void;
}) {
  const t = useTranslations("dashboard");
  const tEsim = useTranslations("esim");
  const locale = useLocale();

  // No ICCID yet means the order is paid and DT One has not delivered it —
  // the eSIM counterpart of a top-up still waiting on its operator.
  const ongoing = !esim.iccid;
  const lifecycle = esimLifecycle(esim);
  const allowance = orderDataAllowance(esim.benefits, tEsim("data-unlimited"));
  const validity = esim.benefits?.validity;
  const validityUnit = validity ? validityUnitKey(validity.unit) : null;
  const isoCode = esim.benefits?.country_set;
  const flag = countryFlag(isoCode);
  const countryName =
    flag && isoCode
      ? new Intl.DisplayNames([locale], { type: "region" }).of(
          isoCode.toUpperCase(),
        )
      : undefined;

  const content = (
    <>
      <div className="flex flex-col gap-3 sm:grid sm:grid-cols-subgrid sm:items-center sm:gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon name="esim" size={20} />
            {flag && (
              <span
                role="img"
                aria-label={countryName}
                title={countryName}
                className="absolute -bottom-1.5 -right-1.5 text-base leading-none"
              >
                {flag}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-text-primary">
              {esim.productName}
            </p>
            {esim.iccid && (
              <p className="font-mono text-sm text-text-muted">
                {formatIccid(esim.iccid)}
              </p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-4 sm:contents">
          {/* Reads like a top-up's amount, so the status is the one pill in
              the row; the validity says the size is the plan's, not what is
              left of it. */}
          {(allowance || validity) && (
            <div className="text-left sm:col-start-2 sm:text-right">
              {allowance && (
                <p className="font-semibold text-text-primary">{allowance}</p>
              )}
              {validity && (
                <p className="text-xs text-text-muted">
                  {validityUnit
                    ? tEsim(`validity-${validityUnit}`, {
                        count: validity.quantity,
                      })
                    : `${validity.quantity} ${validity.unit}`}
                </p>
              )}
            </div>
          )}
          <div className="flex flex-col items-end gap-1 sm:col-start-3">
            <StatusBadge
              label={
                ongoing ? t("topup.ongoing") : tEsim(`status-${lifecycle}`)
              }
              variant={ongoing ? "warning" : LIFECYCLE_VARIANT[lifecycle]}
            />
            <span className="text-xs text-text-muted">
              {tEsim("purchased-on", {
                date: formatEsimDate(esim.purchasedAt, locale),
              })}
            </span>
          </div>
          {onOpen && (
            <Icon
              name="chevronRight"
              size={16}
              className="text-text-muted sm:col-start-4"
            />
          )}
        </div>
      </div>

      {ongoing && (
        <p className="mt-2 text-xs text-text-muted">
          {t("esim.ongoing-message")}
        </p>
      )}
    </>
  );

  const className =
    "block w-full rounded-xl p-4 text-left sm:col-span-full sm:grid sm:grid-cols-subgrid sm:*:col-span-full";

  return onOpen ? (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        className,
        "cursor-pointer transition-colors hover:bg-surface-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50",
      )}
    >
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
}
